// src/pages/DoctorAppointmentDetailPage.jsx
// Route: /doctor/appointments/:appointmentId   (?step=assessment|prescription|orders|review)
//
// Clicking a row in the doctor's appointment list used to navigate to
// /doctor/patients/{patientId} — the patient's record, not the appointment.
// That silently discarded which appointment was clicked, and there was no
// appointment screen to land on instead. This is it.
//
// Reads GET /api/appointments/{id} (appointmentAPI.get) rather than passing the
// row through router state, so the URL is shareable and survives a refresh.
//
// The appointment itself is still READ from the generic endpoint and never
// written through it: AppointmentController's GET/PATCH/DELETE /{id} endpoints
// have no ownership check (see the controller's "{id}-not-{patientId} gap"
// TODOs), so cancel/reschedule are deliberately not offered here. Closing a
// visit without a summary (complete / no-show) goes through the guarded
// PATCH /api/doctor/appointments/{id}/status, from the ⋯ menu.
//
// The page is a thin shell: everything on screen is ConsultationPanel
// (components/doctor/), which composes the header, glance strip, steps and the
// context drawer from hooks/useConsultation.js.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { appointmentAPI } from '../services/api';
import Icon from '../components/Icon';
import ConsultationPanel from '../components/doctor/ConsultationPanel';

export default function DoctorAppointmentDetailPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Appointment — Previu';
    return () => { document.title = 'Previu — Patient Management'; };
  }, []);

  const load = useCallback(async () => {
    if (!appointmentId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await appointmentAPI.get(appointmentId);
      setAppointment(res.data);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? 'That appointment no longer exists.'
          : 'Could not load this appointment. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => { load(); }, [load]);

  const breadcrumbs = [
    { label: 'Appointments', to: '/doctor/appointments' },
    { label: 'Appointment' },
  ];

  if (loading) {
    return (
      <AppShell breadcrumbs={breadcrumbs}>
        <div className="skeleton-list">
          <div className="skeleton skeleton--tabs" />
          <div className="skeleton skeleton--body" />
        </div>
      </AppShell>
    );
  }

  if (error || !appointment) {
    return (
      <AppShell breadcrumbs={breadcrumbs}>
        <div className="empty-state">
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="calendar" size={20} /></div>
          <div className="empty-title">{error || 'Appointment not found.'}</div>
          <button className="btn btn-primary" style={{ marginTop: 16 }}
            onClick={() => navigate('/doctor/appointments')}>
            Back to appointments
          </button>
        </div>
      </AppShell>
    );
  }

  // GET /appointments/{id} returns a flat patientId/patientName pair, while the
  // doctor's list DTO nests a patient object — accept either so this page works
  // regardless of which shape the API hands back.
  const patientId = appointment.patientId ?? appointment.patient?.id;
  const patientName = appointment.patientName
    ?? [appointment.patient?.firstName, appointment.patient?.lastName].filter(Boolean).join(' ');
  const patientPhone = appointment.patient?.phone ?? appointment.patientPhone;

  const onStatusChange = status => setAppointment(a => ({ ...a, status }));

  return (
    <AppShell breadcrumbs={breadcrumbs}>
      <button className="back-link" onClick={() => navigate('/doctor/appointments')}>
        ← Appointments
      </button>

      <ConsultationPanel
        appointment={appointment}
        patientId={patientId}
        patientName={patientName}
        patientPhone={patientPhone}
        onStatusChange={onStatusChange}
      />
    </AppShell>
  );
}
